/**
 * @file FormulationButton.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-07
 * @modified 2026-10-01
 * @description Slide-out button that surfaces an inventory item's formulation,
 *              rendering nothing at all when the item has none.
 *
 *              Wrapped up as one component because both inventory item modals
 *              (InventoryItemModal and SearchInventory's own) need identical
 *              behaviour — the lookup, the registry fetch, the view modal and
 *              the export modal all live here rather than being repeated.
 *
 *              Opens on the LATEST stored formulation regardless of period:
 *              FormulationModal falls back to the newest iteration overall when
 *              given no period, which is exactly what's wanted from an item
 *              screen that has no period context of its own.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import FormulationModal from './FormulationModal';
import FormulationExportModal from './FormulationExportModal';
import { useSlideOutButton } from './SlideOutButton';
import { getAccountName } from '../utils/accountCodes';
import './Formulations.css';

/**
 * @param {number} itemId            inventory item to look up
 * @param {string} [wrapperClassName] when set, the button is wrapped in a div
 *   with this class — used by hosts that have no footer of their own, so that
 *   an item WITHOUT a formulation renders no empty footer bar at all.
 */
function FormulationButton({ itemId, wrapperClassName }) {
  const [info, setInfo] = useState(null);
  const [components, setComponents] = useState([]);
  const [showFormulation, setShowFormulation] = useState(false);
  const [exportIds, setExportIds] = useState(null);
  // The period of the entry the modal was showing when Export was pressed.
  const [exportPeriod, setExportPeriod] = useState(null);

  // Measured here rather than in the host modal: this component mounts in the
  // same render cycle as the button it renders, which is what the hook needs.
  useSlideOutButton([info?.formulation_id]);

  useEffect(() => {
    if (!itemId) { setInfo(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/formulations/by-item/${itemId}`, {
          credentials: 'include'
        });
        if (!res.ok) { if (!cancelled) setInfo(null); return; }  // 404 = no formulation
        const data = await res.json();
        if (!cancelled) setInfo(data);
      } catch (err) {
        console.error('Error checking for formulation:', err);
        if (!cancelled) setInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [itemId]);

  /**
   * The registry, refetched on demand. The modal calls this after creating a
   * new field — without it the field is created on the server and then never
   * rendered, because the row only exists if its key is in `components`.
   */
  const loadComponents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' });
      const data = await res.json();
      setComponents(data.components || []);
    } catch (err) {
      console.error('Error loading formulation components:', err);
    }
  }, []);

  // Registry is only needed once the modal is actually opened.
  const openFormulation = async () => {
    if (!components.length) await loadComponents();
    setShowFormulation(true);
  };

  if (!info?.formulation_id) return null;

  const button = (
    <button className="btn-slideout" onClick={openFormulation} title="View this product's formulation">
      <div className="icon-wrapper">
        <span className="material-symbols-rounded">science</span>
      </div>
      <span className="btn-label">Formulation</span>
    </button>
  );

  return (
    <>
      {wrapperClassName ? <div className={wrapperClassName}>{button}</div> : button}

      {showFormulation && (
        <FormulationModal
          mode="view"
          formulationId={info.formulation_id}
          components={components}
          onComponentsChanged={loadComponents}
          /* No period: the modal then opens on the newest iteration overall. */
          period={null}
          currentPeriod={null}
          onClose={() => setShowFormulation(false)}
          onSaved={() => setShowFormulation(false)}
          onExport={(id, p) => { setExportPeriod(p || null); setExportIds([id]); }}
        />
      )}

      {exportIds && (
        <FormulationExportModal
          ids={exportIds}
          /* Match the period of the iteration being shown, so the sheet isn't
             empty for a product whose latest data predates the current period. */
          period={exportPeriod || info.latest_period}
          accountNames={
            info.sales_account_code && getAccountName(info.sales_account_code)
              ? { [info.sales_account_code]: getAccountName(info.sales_account_code) }
              : {}
          }
          onClose={() => setExportIds(null)}
          onDone={() => setExportIds(null)}
        />
      )}
    </>
  );
}

export default FormulationButton;
